import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MapPin, Truck, Home } from "lucide-react";
import Seo from "@/lib/Seo";
import { api, euros, formatDate } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setNotFound(true); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/order-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "payee") { setOrder(data); return; }
        if (data.payment_status === "echouee") { setFailed(true); return; }
        attempts.current += 1;
        if (attempts.current < 20) setTimeout(poll, 2500);
        else setFailed(true);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <>
      <Seo title="Confirmation de commande" path="/commande/confirmation" />
      <section className="py-16 sm:py-24" data-testid="confirmation-page">
        <div className="container-x max-w-2xl">
          {notFound && (
            <Reveal className="bg-white border border-line p-10 text-center">
              <XCircle className="w-10 h-10 text-rouge mx-auto mb-4" />
              <h1 className="font-display text-2xl font-extrabold uppercase text-onyx">Commande introuvable</h1>
              <p className="mt-3 text-sm text-muted-foreground">Ce lien de confirmation est invalide ou a expiré.</p>
              <Link to="/" className="mt-6 inline-block bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">Retour à l'accueil</Link>
            </Reveal>
          )}

          {failed && !notFound && (
            <Reveal className="bg-white border border-line p-10 text-center" data-testid="confirmation-failed">
              <XCircle className="w-10 h-10 text-rouge mx-auto mb-4" />
              <h1 className="font-display text-2xl font-extrabold uppercase text-onyx">Paiement non abouti</h1>
              <p className="mt-3 text-sm text-muted-foreground">Le paiement a échoué ou a expiré. Aucun montant n'a été débité et aucun stock n'a été réservé.</p>
              <Link to="/acheter" data-testid="confirmation-retry" className="mt-6 inline-block bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">Réessayer ma commande</Link>
            </Reveal>
          )}

          {!order && !failed && !notFound && (
            <div className="bg-white border border-line p-10 text-center" data-testid="confirmation-loading">
              <Loader2 className="w-10 h-10 text-rouge mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl font-extrabold uppercase text-onyx">Confirmation du paiement…</h1>
              <p className="mt-3 text-sm text-muted-foreground">Nous vérifions votre paiement auprès de Stripe. Quelques secondes suffisent.</p>
            </div>
          )}

          {order && (
            <div data-testid="confirmation-success" className="bg-white border border-line overflow-hidden">
              <div className="bg-field text-white p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4" />
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Merci pour votre commande !</h1>
                <p className="mt-2 text-white/85 text-sm">Un email de confirmation vient de vous être envoyé.</p>
              </div>
              <div className="p-8">
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Numéro de commande</dt><dd className="font-mono font-bold text-onyx" data-testid="confirmation-number">{order.number}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Date</dt><dd className="font-semibold text-onyx">{formatDate(order.created_at)}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Produits</dt><dd className="font-semibold text-onyx text-right">{order.items.map((i) => i.label).join(", ")}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Sous-total</dt><dd className="font-semibold text-onyx">{euros(order.subtotal)}</dd></div>
                  {order.discount > 0 && <div className="flex justify-between text-rouge"><dt>Remise{order.promo_code ? ` (${order.promo_code})` : ""}</dt><dd className="font-semibold">-{euros(order.discount)}</dd></div>}
                  <div className="flex justify-between"><dt className="text-muted-foreground">Livraison</dt><dd className="font-semibold text-onyx">{order.shipping_cost === 0 ? "Gratuit" : euros(order.shipping_cost)}</dd></div>
                  <div className="flex justify-between border-t border-line pt-3"><dt className="font-display font-bold uppercase text-onyx">Total payé</dt><dd className="font-display text-xl font-extrabold text-onyx" data-testid="confirmation-total">{euros(order.total)}</dd></div>
                </dl>
                <div className="mt-6 bg-sand p-5 text-sm flex items-start gap-3">
                  {order.delivery_kind === "pickup" && <><MapPin className="w-5 h-5 text-rouge shrink-0" /><span><strong>Retrait chez un partenaire</strong> — {order.pickup_name}. Nous vous prévenons par email dès que votre jeu est prêt (sous 24h).</span></>}
                  {order.delivery_kind === "relay" && <><Truck className="w-5 h-5 text-rouge shrink-0" /><span><strong>Livraison en point relais</strong>{order.relay_name ? ` — ${order.relay_name}` : ""}. Vous recevrez le numéro de suivi par email à l'expédition.</span></>}
                  {order.delivery_kind === "home" && <><Home className="w-5 h-5 text-rouge shrink-0" /><span><strong>Livraison à domicile</strong> — {order.address}, {order.zip} {order.city}. Vous recevrez le numéro de suivi par email à l'expédition.</span></>}
                </div>
                <Link to="/" data-testid="confirmation-home"
                  className="mt-8 block text-center bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
